import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import { track } from '@/helpers/mixpanelDes'
import { http } from '@/helpers/http'
import { TextSelection } from '@tiptap/pm/state'
import type { Node } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/core'
import type { THeadingNode } from '@/types/editor'

export const useEditorStore = defineStore('editor', () => {
  const zoom = ref(1)
  const isReplacePanel = ref<boolean>(false)
  const isHighlightsPanel = ref<boolean>(false)
  const isPageMenuOpen = ref<boolean>(false)
  const isNotesPanel = ref<boolean>(false)
  const isSpeechPanel = ref<boolean>(false)
  const isFontPanel = ref<boolean>(false)
  const isPageNumberingPanel = ref<boolean>(false)
  const isSpeechRecording = ref<boolean>(false)
  const editorObject = shallowRef<Editor | null>(null)
  let tiptapApiKey: string | null = null
  let tiptapToken: string | null = null

  function toggleReplacePanel() {
    isHighlightsPanel.value = false
    isNotesPanel.value = false
    isSpeechPanel.value = false
    isFontPanel.value = false
    isPageNumberingPanel.value = false
    isReplacePanel.value = !isReplacePanel.value
    track('draft-find-and-replace', { open: isReplacePanel.value })
  }

  function toggleHighlightsPanel() {
    isReplacePanel.value = false
    isNotesPanel.value = false
    isSpeechPanel.value = false
    isHighlightsPanel.value = !isHighlightsPanel.value
    track('draft-highlights', { open: isHighlightsPanel.value })
  }

  function toggleNotesPanel() {
    isReplacePanel.value = false
    isHighlightsPanel.value = false
    isSpeechPanel.value = false
    isNotesPanel.value = !isNotesPanel.value
    track('draft-notes', { open: isNotesPanel.value })
  }

  function toggleSpeechPanel() {
    isReplacePanel.value = false
    isHighlightsPanel.value = false
    isNotesPanel.value = false
    isSpeechPanel.value = !isSpeechPanel.value
    track('draft-speech-panel', { open: isSpeechPanel.value })
  }

  function toggleFontPanel() {
    isReplacePanel.value = false
    isHighlightsPanel.value = false
    isNotesPanel.value = false
    isSpeechPanel.value = false
    isPageNumberingPanel.value = false
    isFontPanel.value = !isFontPanel.value
    track('draft-font-panel', { open: isFontPanel.value })
  }

  function togglePageNumberingPanel() {
    isReplacePanel.value = false
    isHighlightsPanel.value = false
    isNotesPanel.value = false
    isSpeechPanel.value = false
    isFontPanel.value = false
    isPageNumberingPanel.value = !isPageNumberingPanel.value
    track('draft-page-numbering-panel', { open: isPageNumberingPanel.value })
  }

  function getUnfinishedNodeData(editor?: Editor | null): Promise<{
    chapterTitle: string
    subChapterTitle: string
    prompt?: string
    from: number
    to: number
  } | null> {
    return new Promise((resolve) => {
      let nodeFound: boolean = false
      editor?.state.doc.forEach((node: Node, pos, index: number) => {
        if (node.attrs.wordgenieTitle && node.attrs.wordgenieSubtitle && !nodeFound) {
          nodeFound = true
          const data = {
            chapterTitle: node.attrs.wordgenieTitle,
            subChapterTitle: node.attrs.wordgenieSubtitle,
            prompt: node.attrs.wordgeniePrompt,
            from: pos,
            to: pos + node.nodeSize,
          }
          resolve(data)
        }
        if (editor?.state.doc.childCount - 1 === index) {
          resolve(null)
        }
      })
    })
  }

  function titleFromContent(editor?: HTMLElement | null): string {
    const content = (editor?.innerText ?? '').replace(/\s+/g, ' ').slice(0, 50)

    const limiterIndex = ['.', '!', '?', ' ']
      .map((e) => content.lastIndexOf(e))
      .filter((e) => e > 10)
      .sort()
      .shift()

    return content.slice(0, limiterIndex).trim()
  }

  function setEditor(editor: Editor | null) {
    editorObject.value = editor
  }

  function getEditor(): Editor | null {
    return editorObject.value
  }

  function isRTL(editor: Editor) {
    const { from, to } = editor.state.selection
    let hasRTL = false

    editor.state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.isBlock) {
        const domElement = editor.view.nodeDOM(pos)
        if (domElement instanceof HTMLElement) {
          const effectiveDirection = window.getComputedStyle(domElement).direction
          if (effectiveDirection === 'rtl') {
            hasRTL = true
            return false
          }
        }
      }
      return true
    })

    return hasRTL
  }

  function setParagraph(editor: Editor) {
    const textDirection = isRTL(editor) ? 'rtl' : 'ltr'
    const unwrap = editor.chain().focus() as any

    if (editor.isActive('blockquote')) {
      unwrap.unsetBlockquote()
    } else if (editor.isActive('codeBlock')) {
      unwrap.toggleCodeBlock()
    } else if (editor.isActive('bulletList')) {
      unwrap.toggleBulletList()
    } else if (editor.isActive('orderedList')) {
      unwrap.toggleOrderedList()
    }

    unwrap.run()

    const chain = editor.chain().focus() as any

    chain.setParagraph().setTextDirection(textDirection).run()
  }

  function toggleBlockquote(editor: Editor) {
    const textDirection = isRTL(editor) ? 'rtl' : 'ltr'
    const chain = editor.chain().focus() as any

    if (editor.isActive('codeBlock')) {
      chain.toggleCodeBlock()
    } else if (editor.isActive('bulletList')) {
      chain.toggleBulletList()
    } else if (editor.isActive('orderedList')) {
      chain.toggleOrderedList()
    }

    chain.toggleBlockquote().setTextDirection(textDirection).run()
  }

  function getCurrentBlock(editor: Editor) {
    if (!editor) return null

    const { state } = editor
    const { selection } = state
    const { $from } = selection

    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth)
      if (node.type.isBlock) {
        return {
          node,
          pos: $from.before(depth),
          depth,
        }
      }
    }

    return null
  }

  function extendSelectionToBlock(editor: Editor) {
    const { state, view } = editor
    const { selection } = state
    const { $from } = selection

    // Find the topmost block node
    let depth = $from.depth
    while ($from.node(depth).isInline && depth > 0) {
      depth--
    }
    const block = $from.node(depth)
    const pos = $from.start(depth)

    // Create a new selection for the entire block
    const newSelection = TextSelection.create(state.doc, pos, pos + block.nodeSize - 1)

    // Apply the new selection
    view.dispatch(state.tr.setSelection(newSelection))
  }

  function getWordgenieProgress(): number {
    const editor = getEditor()
    if (!editor) return 0

    let total = 0
    let todo = 0

    editor.state.doc.descendants((node) => {
      // subchapters
      if (node.type.name === 'heading' && node.attrs.level === 3) {
        total += 1
      }

      // paragraphs to be generated
      if (node.type.name === 'paragraph' && node.attrs.wordgenieSubtitle) {
        todo += 1
      }
    })

    return total === 0 ? 100 : Math.round(((total - todo) / total) * 100)
  }

  async function fetchTiptapCredentials() {
    if (tiptapApiKey && tiptapToken) {
      return {
        apiKey: tiptapApiKey,
        token: tiptapToken,
      }
    }

    try {
      const res = await http.get<{
        apiKey: string
        token: string
      }>('/get_tip_tap_credentials')

      tiptapApiKey = res.apiKey
      tiptapToken = res.token

      return res
    } catch (error) {
      console.error('Failed to fetch Tiptap credentials', error)
      throw error
    }
  }

  function generateHeadingsTree(editor: Editor): THeadingNode[] {
    const headings: THeadingNode[] = []
    const stack: THeadingNode[] = []

    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'heading' && node.attrs.level >= 2 && node.attrs.level <= 6) {
        const heading: THeadingNode = {
          content: node.textContent,
          level: node.attrs.level,
          page: getPageForPosition(editor, pos + 1),
          pos,
        }

        while (stack.length > 0 && (stack[stack.length - 1] as THeadingNode).level >= heading.level) {
          stack.pop()
        }

        if (stack.length === 0) {
          headings.push(heading)
        } else {
          const parent = stack[stack.length - 1] as THeadingNode
          if (!parent.children) {
            parent.children = []
          }
          parent.children?.push(heading)
        }

        stack.push(heading)
      }
    })

    return headings
  }

  function getPageForPosition(editor: Editor, pos: number): number | undefined {
    if (editor.storage?.pages && typeof editor.storage.pages.getPageForPosition === 'function') {
      try {
        return editor.storage.pages.getPageForPosition(pos)
      } catch {
        return
      }
    }

    if (typeof editor.commands.getPageForPosition === 'function') {
      try {
        const result = editor.commands.getPageForPosition(pos)
        if (typeof result === 'number') return result
      } catch {
        return
      }
    }
  }

  function removeEmptyParagraphs() {
    const editor = getEditor()
    if (!editor) return

    const { doc } = editor.state
    const paragraphsToRemove: Array<{ pos: number; nodeSize: number }> = []
    doc.descendants((node, pos) => {
      if (
        node.type.name === 'paragraph' &&
        (!node.textContent || node.textContent.trim() === '') &&
        node.attrs.wordgenieTitle
      ) {
        paragraphsToRemove.push({ pos, nodeSize: node.nodeSize })
      }
    })

    let tr = editor.state.tr
    for (let i = paragraphsToRemove.length - 1; i >= 0; i--) {
      const paragraph = paragraphsToRemove[i]
      if (!paragraph) continue
      const { pos, nodeSize } = paragraph
      tr = tr.deleteRange(pos, pos + nodeSize)
    }
    if (paragraphsToRemove.length > 0) {
      editor.view.dispatch(tr)
    }
  }

  return {
    isReplacePanel,
    isHighlightsPanel,
    isNotesPanel,
    isSpeechPanel,
    isFontPanel,
    isPageNumberingPanel,
    isSpeechRecording,
    removeEmptyParagraphs,
    isRTL,
    editorObject,
    setEditor,
    getEditor,
    getCurrentBlock,
    setParagraph,
    toggleBlockquote,
    toggleReplacePanel,
    toggleHighlightsPanel,
    toggleNotesPanel,
    toggleSpeechPanel,
    toggleFontPanel,
    togglePageNumberingPanel,
    getUnfinishedNodeData,
    titleFromContent,
    extendSelectionToBlock,
    getWordgenieProgress,
    fetchTiptapCredentials,
    generateHeadingsTree,
    getPageForPosition,
    isPageMenuOpen,
    zoom,
  }
})
